/* Desenho dos gráficos personalizados (GET /agregado) em SVG/HTML, para qualquer grupo e série. */

import { esc, nf, pct } from '../core/formato';
import type { Agregado, TipoGrafico } from '../tipos/api';
import { vazio } from './componentes';

/** Paleta categórica (tokens --cat-1…12 em tokens.css, com versão para o tema escuro). */
const PALETA = Array.from({ length: 12 }, (_, i) => `var(--cat-${i + 1})`);
const COR_ESCOPO: Record<string, string> = { 'Escopo 1': 'var(--esc1)', 'Escopo 2': 'var(--esc2)', 'Escopo 3': 'var(--esc3)' };

function cores(nomes: (string | null)[], dimensao: string | null): string[] {
  return nomes.map((n, i) => (dimensao === 'escopo' && n && COR_ESCOPO[n]) || PALETA[i % PALETA.length]);
}

const casas = (a: Agregado) => (a.unidade === 'lançamentos' ? 0 : 2);
const curto = (s: string, n = 14) => (s.length > n ? `${s.slice(0, n - 1)}…` : s);

/** Matriz grupo × série a partir dos itens. */
function matriz(a: Agregado): number[][] {
  const idx = new Map(a.itens.map((i) => [`${i.grupo}\u0000${i.serie}`, i.valor]));
  return a.grupos.map((g) => a.series.map((s) => idx.get(`${g}\u0000${s}`) || 0));
}

function legenda(nomes: string[], cs: string[]): string {
  return `<div class="legend" style="justify-content:flex-start;margin:0 0 14px">${nomes.map((n, i) =>
    `<span><i style="background:${cs[i]}"></i>${esc(n)}</span>`).join('')}</div>`;
}

function barrasVerticais(a: Agregado, m: number[][], cs: string[]): string {
  const W = 640, h = 300, pad = { l: 58, r: 12, t: 14, b: 48 };
  const somas = m.map((l) => l.reduce((x, v) => x + v, 0));
  const max = Math.max(1e-9, ...somas) * 1.12;
  const bw = (W - pad.l - pad.r) / Math.max(1, a.grupos.length);
  const y = (v: number) => pad.t + (h - pad.t - pad.b) * (1 - v / max);
  const muitos = a.grupos.length > 8;
  let g = '';
  for (let i = 0; i <= 4; i++) {
    const v = (max * i) / 4, yy = y(v);
    g += `<line x1="${pad.l}" x2="${W - pad.r}" y1="${yy}" y2="${yy}" stroke="var(--line-2)"/><text x="${pad.l - 10}" y="${yy + 4}" text-anchor="end" class="bar-tip" font-size="13">${nf(v, max > 10 ? 0 : 1)}</text>`;
  }
  a.grupos.forEach((grupo, i) => {
    const x = pad.l + i * bw + bw * 0.18, w = bw * 0.64;
    let acc = 0;
    m[i].forEach((v, k) => {
      const y0 = y(acc + v), y1 = y(acc);
      const serie = a.series[k];
      g += `<rect x="${x}" y="${y0}" width="${w}" height="${Math.max(y1 - y0, 0)}" fill="${cs[k]}"><title>${esc(grupo)}${serie ? ` · ${esc(serie)}` : ''}: ${nf(v, casas(a))} ${a.unidade}</title></rect>`;
      acc += v;
    });
    g += `<text x="${x + w / 2}" y="${h - 26}" text-anchor="middle" class="bar-tip" font-size="${muitos ? 11 : 13}"><title>${esc(grupo)}</title>${esc(curto(grupo, muitos ? 7 : 12))}</text>`;
  });
  return `<svg viewBox="0 0 ${W} ${h}" style="width:100%;height:auto" role="img" aria-label="${esc(a.metrica_nome)} por ${esc(a.dimensao_nome)}">${g}</svg>`;
}

function barrasHorizontais(a: Agregado, m: number[][], cs: string[]): string {
  const somas = m.map((l) => l.reduce((x, v) => x + v, 0));
  const max = Math.max(1e-9, ...somas);
  return `<div style="display:flex;flex-direction:column;gap:14px">${a.grupos.map((grupo, i) => {
    const t = somas[i];
    return `<div>
      <div class="between" style="margin-bottom:6px"><span style="font-weight:600;font-size:13.5px">${esc(grupo)}</span>
        <span class="num" style="font-weight:700;font-size:13.5px;white-space:nowrap">${nf(t, casas(a))} ${a.unidade}</span></div>
      <div class="stack" style="width:${((t / max) * 100).toFixed(1)}%">${m[i].map((v, k) =>
        `<i style="width:${t ? ((v / t) * 100).toFixed(2) : 0}%;background:${cs[k]}" title="${esc(a.series[k] || grupo)}: ${nf(v, casas(a))} ${a.unidade}"></i>`).join('')}</div>
    </div>`;
  }).join('')}</div>`;
}

function linhas(a: Agregado, m: number[][], cs: string[]): string {
  const W = 640, h = 300, pad = { l: 58, r: 18, t: 14, b: 48 };
  const max = Math.max(1e-9, ...m.flat()) * 1.1;
  const n = Math.max(1, a.grupos.length - 1);
  const x = (i: number) => pad.l + (W - pad.l - pad.r) * (i / n);
  const y = (v: number) => pad.t + (h - pad.t - pad.b) * (1 - v / max);
  let g = '';
  for (let i = 0; i <= 4; i++) {
    const v = (max * i) / 4, yy = y(v);
    g += `<line x1="${pad.l}" x2="${W - pad.r}" y1="${yy}" y2="${yy}" stroke="var(--line-2)"/><text x="${pad.l - 10}" y="${yy + 4}" text-anchor="end" class="bar-tip" font-size="13">${nf(v, max > 10 ? 0 : 1)}</text>`;
  }
  a.grupos.forEach((grupo, i) => (g += `<text x="${x(i)}" y="${h - 26}" text-anchor="middle" class="bar-tip" font-size="13"><title>${esc(grupo)}</title>${esc(curto(grupo, 8))}</text>`));
  a.series.forEach((serie, k) => {
    g += `<polyline points="${a.grupos.map((_, i) => `${x(i)},${y(m[i][k])}`).join(' ')}" fill="none" stroke="${cs[k]}" stroke-width="2.2"/>`;
    a.grupos.forEach((grupo, i) => (g += `<circle cx="${x(i)}" cy="${y(m[i][k])}" r="3.2" fill="${cs[k]}"><title>${esc(grupo)}${serie ? ` · ${esc(serie)}` : ''}: ${nf(m[i][k], casas(a))} ${a.unidade}</title></circle>`));
  });
  return `<svg viewBox="0 0 ${W} ${h}" style="width:100%;height:auto" role="img" aria-label="${esc(a.metrica_nome)} por ${esc(a.dimensao_nome)}">${g}</svg>`;
}

function pizza(a: Agregado, m: number[][]): string {
  const valores = m.map((l) => l.reduce((x, v) => x + v, 0));
  const total = valores.reduce((x, v) => x + v, 0);
  const cs = cores(a.grupos, a.dimensao);
  const size = 220, r = size / 2 - 16, c = size / 2, C = 2 * Math.PI * r;
  let off = 0, g = '';
  valores.forEach((v, i) => {
    const len = C * (v / total);
    if (len <= 0) return;
    g += `<circle cx="${c}" cy="${c}" r="${r}" fill="none" stroke="${cs[i]}" stroke-width="30" stroke-dasharray="${Math.max(len - 1.5, 0)} ${C - len + 1.5}" stroke-dashoffset="${-off}" transform="rotate(-90 ${c} ${c})"><title>${esc(a.grupos[i])}: ${nf(v, casas(a))} ${a.unidade} (${pct(v, total)})</title></circle>`;
    off += len;
  });
  const svg = `<svg viewBox="0 0 ${size} ${size}" style="width:${size}px;height:${size}px;flex:none">${g}
    <text x="${c}" y="${c - 2}" text-anchor="middle" font-size="22" font-weight="800" fill="var(--ink)" class="num">${nf(total, total > 100 ? 0 : casas(a))}</text>
    <text x="${c}" y="${c + 18}" text-anchor="middle" font-size="11" font-weight="600" fill="var(--muted)">${esc(a.unidade)}</text></svg>`;
  const itens = a.grupos.map((grupo, i) => `<div class="between" style="gap:10px;font-size:13px">
      <span style="display:flex;align-items:center;gap:8px;min-width:0"><i style="width:11px;height:11px;border-radius:3px;flex:none;background:${cs[i]}"></i><span style="overflow:hidden;text-overflow:ellipsis;white-space:nowrap" title="${esc(grupo)}">${esc(grupo)}</span></span>
      <span class="num" style="white-space:nowrap;font-weight:600">${nf(valores[i], casas(a))} · ${pct(valores[i], total)}</span></div>`).join('');
  return `<div style="display:flex;gap:28px;align-items:center;flex-wrap:wrap">${svg}<div style="flex:1;min-width:220px;display:flex;flex-direction:column;gap:8px">${itens}</div></div>`;
}

function tabelaPivo(a: Agregado, m: number[][]): string {
  const comSeries = Boolean(a.dimensao2);
  const somaLinha = m.map((l) => l.reduce((x, v) => x + v, 0));
  const somaColuna = a.series.map((_, k) => m.reduce((x, l) => x + l[k], 0));
  const cab = comSeries ? a.series.map((s) => `<th class="r">${esc(s)}</th>`).join('') : '';
  return `<div class="table-wrap" style="overflow-x:auto"><table class="table">
    <thead><tr><th>${esc(a.dimensao_nome)}</th>${cab}<th class="r">Total (${esc(a.unidade)})</th><th class="r">%</th></tr></thead>
    <tbody>${a.grupos.map((grupo, i) => `<tr><td><b>${esc(grupo)}</b></td>${comSeries ? m[i].map((v) => `<td class="r num">${nf(v, casas(a))}</td>`).join('') : ''}
      <td class="r num"><b>${nf(somaLinha[i], casas(a))}</b></td><td class="r num">${pct(somaLinha[i], a.total)}</td></tr>`).join('')}</tbody>
    <tfoot><tr><td>Total</td>${comSeries ? somaColuna.map((v) => `<td class="r num">${nf(v, casas(a))}</td>`).join('') : ''}<td class="r num">${nf(a.total, casas(a))}</td><td class="r num">100%</td></tr></tfoot>
  </table></div>`;
}

export function graficoPersonalizado(a: Agregado, tipo: TipoGrafico = a.tipo): string {
  if (!a.total) return vazio('Sem dados para essa combinação com os filtros atuais.');
  const m = matriz(a);
  if (tipo === 'pizza') return pizza(a, m);
  if (tipo === 'tabela') return tabelaPivo(a, m);
  const cs = a.dimensao2 ? cores(a.series, a.dimensao2) : ['var(--marca-500)'];
  const leg = a.dimensao2 ? legenda(a.series.map(String), cs) : '';
  if (tipo === 'linhas') {
    // Meses finais sem nenhum lançamento (ainda não aconteceram) não viram uma queda a zero.
    let fim = a.grupos.length;
    if (a.dimensao === 'mes') while (fim > 1 && m[fim - 1].every((v) => !v)) fim--;
    return leg + linhas({ ...a, grupos: a.grupos.slice(0, fim) }, m.slice(0, fim), cs);
  }
  if (tipo === 'barras_horizontais') return leg + barrasHorizontais(a, m, cs);
  return leg + barrasVerticais(a, m, cs);
}
